import classNames from 'classnames';

import {
  FlyoutContainer,
  FlyoutPanel,
} from 'components/shared/side-menu/flyout-container';
import { useHoverFlyout } from 'components/shared/side-menu/hooks';

import { filterVisibleItems } from 'components/shared/side-menu/side-menu.utils';

import type { NavItem } from 'components/shared/side-menu/nav-config';

interface BaseCollapsedFlyoutProps {
  item: NavItem;
  baseItemClasses: string;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  isItemActive?: (item: NavItem) => boolean;
  onNavigate?: () => void;
}

const BaseCollapsedFlyout = ({
  item,
  baseItemClasses,
  renderLink,
  isItemActive,
  onNavigate,
}: BaseCollapsedFlyoutProps) => {
  const { isHovered, flyoutTop, hoverProps, closeFlyout } = useHoverFlyout();

  const visibleChildren = filterVisibleItems(item.children || []);

  const handleClick = () => {
    closeFlyout();

    if (onNavigate) onNavigate();
  };

  return (
    <div {...hoverProps} className="relative">
      <button
        className={classNames(baseItemClasses, 'w-full')}
        aria-expanded={isHovered}
        aria-haspopup="true"
        aria-label={item.label}
      >
        <span aria-hidden="true">{item.icon}</span>
      </button>

      <FlyoutContainer isVisible={isHovered} top={flyoutTop}>
        <FlyoutPanel className="py-2 min-w-[200px]" arrowClassName="h-4 mt-2">
          <div className="px-4 py-2 font-semibold text-gray-400 text-sm">
            {item.label}
          </div>
          <div className="px-2 pt-1">
            {visibleChildren.map((child) => {
              const isActive =
                typeof isItemActive === 'function' && isItemActive(child);

              return renderLink({
                key: child.id,
                to: child.to,
                end: child.exactMatch,
                onClick: handleClick,
                className: classNames(
                  'flex items-center gap-3 px-3 py-2 text-base font-semibold hover:bg-navy-50 transition-colors rounded-lg',
                  {
                    'text-blue-700 bg-blue-50': isActive,
                    'text-gray-700': !isActive,
                  }
                ),
                children: (
                  <>
                    {child.icon && (
                      <span className="text-gray-700" aria-hidden="true">
                        {child.icon}
                      </span>
                    )}
                    <span className="truncate">{child.label}</span>
                  </>
                ),
              });
            })}
          </div>
        </FlyoutPanel>
      </FlyoutContainer>
    </div>
  );
};

export default BaseCollapsedFlyout;
